import { create } from 'zustand';
import { channelService } from '../react-app/services/apiService.js';

export const useChannelStore = create((set, get) => ({
  channels: [],
  currentChannelId: null,
  isLoading: false,
  error: null,
  unreadCounts: {},     // { channelId: number }
  mentionCounts: {},    // { channelId: number } — @mentioned specifically
  serverUnreads: {},    // { serverId: number } — aggregate for server dots
  channelServerMap: {}, // { channelId: serverId } — for reverse lookup

  // Fetch channels for a server
  fetchChannels: async (serverId) => {
    if (!serverId) {
      set({ channels: [], currentChannelId: null });
      return;
    }
    if (serverId.startsWith('demo-')) return;

    set({ isLoading: true, error: null });
    try {
      const response = await channelService.list(serverId);
      const channelsList = response.data || response;
      const list = Array.isArray(channelsList) ? channelsList : [];
      // Build reverse lookup map
      const newMap = {};
      list.forEach(c => { newMap[c.id] = serverId; });
      set(state => ({
        channels: list,
        isLoading: false,
        channelServerMap: { ...state.channelServerMap, ...newMap },
      }));
      if (list.length > 0 && !get().currentChannelId) {
        set({ currentChannelId: list[0].id });
      }
    } catch (error) {
      set({ error: error.message, isLoading: false });
    }
  },

  loadDemoChannels: (channels) => {
    set({ channels, isLoading: false, error: null });
    if (channels.length > 0) set({ currentChannelId: channels[0].id });
  },

  setCurrentChannel: (channelId) => {
    set(state => ({
      currentChannelId: channelId,
      unreadCounts:  { ...state.unreadCounts,  [channelId]: 0 },
      mentionCounts: { ...state.mentionCounts, [channelId]: 0 },
    }));
  },

  getCurrentChannel: () => {
    const { channels, currentChannelId } = get();
    return channels.find(c => c.id === currentChannelId);
  },

  incrementUnread: (channelId, isMention = false) => {
    const { currentChannelId, channelServerMap } = get();
    if (channelId === currentChannelId) return;
    const serverId = channelServerMap[channelId];
    set(state => ({
      unreadCounts: { ...state.unreadCounts, [channelId]: (state.unreadCounts[channelId] || 0) + 1 },
      ...(isMention ? { mentionCounts: { ...state.mentionCounts, [channelId]: (state.mentionCounts[channelId] || 0) + 1 } } : {}),
      ...(serverId ? { serverUnreads: { ...state.serverUnreads, [serverId]: (state.serverUnreads[serverId] || 0) + 1 } } : {}),
    }));
  },

  clearServerUnread: (serverId) => {
    set(state => ({ serverUnreads: { ...state.serverUnreads, [serverId]: 0 } }));
  },

  markChannelRead: (channelId) => {
    set(state => ({
      unreadCounts:  { ...state.unreadCounts,  [channelId]: 0 },
      mentionCounts: { ...state.mentionCounts, [channelId]: 0 },
    }));
  },

  getUnreadCount:  (channelId) => get().unreadCounts[channelId]  || 0,
  getMentionCount: (channelId) => get().mentionCounts[channelId] || 0,

  addChannel: (channel) =>
    set(state => ({ channels: [...state.channels, channel] })),

  removeChannel: (channelId) =>
    set(state => ({
      channels: state.channels.filter(c => c.id !== channelId),
      currentChannelId: state.currentChannelId === channelId ? null : state.currentChannelId,
    })),

  updateChannel: (channelId, updates) =>
    set(state => ({
      channels: state.channels.map(c => c.id === channelId ? { ...c, ...updates } : c),
    })),
}));
